import type { JSX } from 'preact'
import type { DetectedInvoice } from '../types'

type Props = {
  invoice: DetectedInvoice
  index: number
  filename: string
  selected: boolean
  active: boolean
  disabled?: boolean
  onToggle: () => void
  onActivate: () => void
  onFilenameChange: (value: string) => void
}

/**
 * Single row in the left review list. Mirrors the paid product's
 * PdfSplitEditorModal invoice cards: checkbox + metadata + confidence bar.
 * Adds a filename input since the marketing tool's job is to deliver named
 * files.
 */
export default function InvoiceListItem({
  invoice,
  index,
  filename,
  selected,
  active,
  disabled,
  onToggle,
  onActivate,
  onFilenameChange,
}: Props) {
  const classes = [
    'sis-listitem',
    active ? 'sis-listitem--active' : '',
    selected ? '' : 'sis-listitem--unchecked',
  ]
    .filter(Boolean)
    .join(' ')

  // Clicking the row activates the preview, but clicks on inputs/checkboxes
  // shouldn't propagate (would re-fire activation needlessly).
  const onRowClick = () => {
    if (!disabled) onActivate()
  }

  const stop = (e: JSX.TargetedMouseEvent<HTMLElement>) => e.stopPropagation()

  return (
    <li class={classes} onClick={onRowClick}>
      <div class="sis-listitem-head">
        <label class="sis-listitem-check" onClick={stop}>
          <input
            type="checkbox"
            checked={selected}
            disabled={disabled}
            onChange={onToggle}
            aria-label={`Select invoice ${index}`}
          />
        </label>
        <div class="sis-listitem-title">
          <strong>Invoice {index}</strong>
          <span class="sis-listitem-pages">
            {pagesLabel(invoice.pages)}
          </span>
        </div>
      </div>

      <dl class="sis-listitem-meta">
        {invoice.vendor && <Row label="Vendor" value={invoice.vendor} />}
        {(invoice.amount || invoice.currency) && (
          <Row
            label="Amount"
            value={formatAmount(invoice.amount, invoice.currency)}
          />
        )}
        {invoice.date && <Row label="Date" value={invoice.date} />}
      </dl>

      <input
        type="text"
        class="sis-input sis-listitem-filename"
        value={filename}
        disabled={disabled || !selected}
        placeholder="filename.pdf"
        onClick={stop}
        onInput={(e: JSX.TargetedEvent<HTMLInputElement, Event>) =>
          onFilenameChange(e.currentTarget.value)
        }
        aria-label={`Filename for invoice ${index}`}
      />
    </li>
  )
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div class="sis-listitem-row">
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </div>
  )
}

function pagesLabel(pages: number[]): string {
  if (pages.length === 0) return ''
  if (pages.length === 1) return `Page ${pages[0]}`
  return `Pages ${pages[0]}–${pages[pages.length - 1]}`
}

function formatAmount(amount: string | null, currency: string | null): string | null {
  if (!amount) return null
  return currency ? `${amount} ${currency}` : amount
}

